import { html, LitElement, type TemplateResult } from 'lit'

interface LocaleMeta {
  lang_code: string
  locale_code: string
  locale_flag: string
  native_name: string
}

interface LanguageData {
  id: string
  meta: { locale_code: string; locale_flag: string; name_en: string; name_native: string; rtl?: boolean }
  fields: string[]
  notes: string[][]
}

interface Strings {
  [key: string]: string
}

const DEFAULT_USER_LANG = 'en-US'

const LOCALE_CODES = [
  'af-ZA',
  'ar-EG',
  'bg-BG',
  'ca-ES',
  'cs-CZ',
  'da-DK',
  'de-DE',
  'el-GR',
  'en-GB',
  'en-US',
  'es-ES',
  'es-MX',
  'et-EE',
  'fa-IR',
  'fi-FI',
  'fr-FR',
  'he-IL',
  'hi-IN',
  'hu-HU',
  'id-ID',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'lt-LT',
  'lv-LV',
  'nb-NO',
  'nl-NL',
  'pl-PL',
  'pt-BR',
  'pt-PT',
  'ro-RO',
  'ru-RU',
  'sk-SK',
  'sl-SI',
  'sv-SE',
  'th-TH',
  'tr-TR',
  'uk-UA',
  'ur-PK',
  'vi-VN',
  'zh-CN',
  'zh-HK',
  'zh-TW',
]

export class FlashcardApp extends LitElement {
  private locales: LocaleMeta[] = []
  private strings: Strings = {}
  private noteData: LanguageData | null = null
  private userLangCode = DEFAULT_USER_LANG
  private noteLangCode = ''
  private currentIndex = 0
  private showAnswer = false
  private loading = true
  private error = false

  protected override createRenderRoot() {
    return this
  }

  override async connectedCallback() {
    super.connectedCallback()

    const params = new URLSearchParams(location.search)
    this.userLangCode = params.get('user') || navigator.language || DEFAULT_USER_LANG
    const noteParam = params.get('note')
    const indexParam = params.get('i')
    this.currentIndex = indexParam ? parseInt(indexParam, 10) : 0

    this.noteLangCode = noteParam ?? LOCALE_CODES[Math.floor(Math.random() * LOCALE_CODES.length)]

    try {
      const [localesResp, stringsResp, noteResp] = await Promise.all([
        fetch('/static/locale/index.json'),
        fetch(`/static/locale/strings/${this.userLangCode}.json`),
        fetch(`/static/locale/${this.noteLangCode}.json`),
      ])
      this.locales = await localesResp.json()
      this.strings = await stringsResp.json()
      this.noteData = await noteResp.json()
      this.loading = false
      this.#updateLangControls()
    } catch {
      this.loading = false
      this.error = true
    }
    this.requestUpdate()
  }

  #updateLangControls() {
    const langControls = document.querySelector('#lang-controls')
    if (!langControls || !this.locales.length) return
    langControls.innerHTML = `
      <select id="user-lang-selector" title="UI Language">
        ${
      this.locales
        .map(
          (l) =>
            `<option value="${l.locale_code}" ${l.locale_code === this.userLangCode ? 'selected' : ''}>
            ${l.locale_flag} ${l.native_name}
          </option>`,
        )
        .join('')
    }
      </select>
      <select id="note-lang-selector" title="${this.strings['card-selector-label'] ?? 'Flashcard Language'}">
        ${
      this.locales
        .map(
          (l) =>
            `<option value="${l.locale_code}" ${l.locale_code === this.noteLangCode ? 'selected' : ''}>
            ${l.locale_flag} ${this.strings[l.locale_code] ?? l.native_name}
          </option>`,
        )
        .join('')
    }
      </select>
    `
    document.getElementById('user-lang-selector')?.addEventListener('change', (e) => {
      this.userLangCode = (e.target as HTMLSelectElement).value
      this.#setParam('user', this.userLangCode)
      location.assign(location.href)
    })
    document.getElementById('note-lang-selector')?.addEventListener('change', (e) => {
      this.noteLangCode = (e.target as HTMLSelectElement).value
      this.#setParam('note', this.noteLangCode)
      location.assign(location.href)
    })
  }

  #setParam(key: string, value: string) {
    const url = new URL(location.href)
    const params = new URLSearchParams(location.search)
    params.set(key, value)
    url.search = params.toString()
    history.replaceState(null, '', url)
  }

  private get notes() {
    return this.noteData?.notes ?? []
  }

  private get currentNote() {
    return this.notes[this.currentIndex]
  }

  #nextCard() {
    this.showAnswer = false
    this.currentIndex = (this.currentIndex + 1) % this.notes.length
    const params = new URLSearchParams(location.search)
    params.set('i', String(this.currentIndex))
    const url = new URL(location.href)
    url.search = params.toString()
    history.replaceState(null, '', url)
    this.requestUpdate()
  }

  async #showAnswer() {
    this.showAnswer = true
    this.requestUpdate()
    await this.updateComplete
    this.querySelector<HTMLAudioElement>('#note-audio')?.play()
  }

  #buildNoteOptions(): TemplateResult {
    const categorized: Record<string, { note: string[]; index: number }[]> = {}
    this.notes.forEach((note, i) => {
      const cat = note[0]
      if (!categorized[cat]) categorized[cat] = []
      categorized[cat].push({ note, index: i })
    })
    return html`
      ${Object.entries(categorized).map(
        ([cat, items]) =>
          html`
            <optgroup label="${cat}">
              ${items.map(
                ({ note, index }) =>
                  html`
                    <option value="${index}" ?selected="${index === this.currentIndex}">${note[1]}</option>
                  `,
              )}
            </optgroup>
          `,
      )}
    `
  }

  override render(): TemplateResult {
    if (this.loading) {
      return html`
        <div class="loading"><ui-spinner size="lg"></ui-spinner></div>
      `
    }
    if (this.error) {
      return html`
        <p class="error-message">Failed to load flashcard data.</p>
      `
    }
    if (!this.noteData || !this.notes.length) {
      return html`
        <p class="error-message">No flashcard data available.</p>
      `
    }

    const note = this.currentNote
    if (!note) {
      return html`
        <p class="error-message">No cards found.</p>
      `
    }

    const [_category, emoji, text, hint] = note
    const audioFilename = getAudioFilename(this.noteLangCode, emoji, text)
    const audioURL = `https://static.bpev.me/apps/flashcards/${this.noteLangCode}/audio/${audioFilename}`
    const downloadURL =
      `https://static.bpev.me/apps/flashcards/${this.noteLangCode}/emoji-flashcards-${this.noteLangCode}.apkg`

    return html`
      <div class="flashcard-container">
        <div
          class="card"
          @click="${this.showAnswer ? this.#nextCard.bind(this) : this.#showAnswer.bind(this)}"
        >
          <div class="card-front">
            <h1 class="emoji">${emoji}</h1>
          </div>
          ${this.showAnswer
            ? html`
              <div class="card-back">
                ${hint
                  ? html`
                    <p class="hint">${hint}</p>
                  `
                  : ''}
                <h2 class="answer-text">${text}</h2>
                <audio id="note-audio" src="${audioURL}"></audio>
                <p class="card-action">${this.strings.next ?? 'next'} →</p>
              </div>
            `
            : html`
              <div class="card-back hidden">
                <p class="card-action">${this.strings['show-answer'] ?? 'show answer'}</p>
              </div>
            `}
        </div>

        <div class="card-nav">
          <select
            id="note-selector"
            @change="${(e: Event) => {
              const idx = parseInt((e.target as HTMLSelectElement).value, 10)
              this.currentIndex = idx
              this.showAnswer = false
              this.requestUpdate()
            }}"
          >
            ${this.#buildNoteOptions()}
          </select>
        </div>

        <div class="download-section">
          <a href="${downloadURL}" download>
            ${this.noteData.meta.locale_flag} ${this.strings.download ?? 'Download flashcards'}
          </a>
        </div>
      </div>
    `
  }
}

function getAudioFilename(language: string, emoji: string, text: string): string {
  const illegalRe = /[\/\?<>\\:\*\|"]/g
  // deno-lint-ignore no-control-regex
  const controlRe = /[\x00-\x1f\x80-\x9f]/g
  const reservedRe = /^\.+$/
  const windowsReservedRe = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i
  const windowsTrailingRe = /[\. ]+$/
  const replacement = ''
  return `${language}_${emoji}_${text}.mp3`
    .toLowerCase()
    .normalize('NFC')
    .replace(illegalRe, replacement)
    .replace(controlRe, replacement)
    .replace(reservedRe, replacement)
    .replace(windowsReservedRe, replacement)
    .replace(windowsTrailingRe, replacement)
    .replace(/[,;:\s()]+/g, '-')
}

customElements.define('flashcard-app', FlashcardApp)
